import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { 
  galleryGrids,
  gallerySpacing,
  galleryColors
} from '../../styles/galleryHelpers';

// Base grid container with professional spacing
const GridContainer = styled.div<{ $galleryMode?: boolean; $variant?: string }>`
  width: 100%;
  
  ${({ $galleryMode, $variant }) => {
    if (!$galleryMode) {
      // Standard grid - backward compatible
      return css`
        display: grid;
        gap: ${({ theme }) => theme.spacing.lg};
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        
        @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
          gap: ${({ theme }) => theme.spacing.xl};
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
        }
      `;
    }
    
    // Gallery mode with professional styling
    if ($variant === 'masonry') {
      return galleryGrids.masonryGrid;
    } else {
      return galleryGrids.artworkGrid;
    }
  }}
`;

// This component is now handled by GridContainer with variant="masonry"

// Gallery section wrapper with professional spacing
const GallerySection = styled(motion.section)<{ $galleryMode?: boolean }>`
  ${({ $galleryMode }) => $galleryMode && css`
    margin-bottom: ${gallerySpacing.section};
    
    &:first-child {
      margin-top: ${gallerySpacing.lg};
    }
  `}
`;

// Professional section header for gallery
const SectionHeader = styled.div<{ $galleryMode?: boolean }>`
  ${({ $galleryMode }) => $galleryMode && css`
    margin-bottom: ${gallerySpacing.lg};
    text-align: center;
    
    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
      text-align: left;
    }
  `}
`;

const SectionTitle = styled.h2<{ $galleryMode?: boolean }>`
  ${({ $galleryMode, theme }) => $galleryMode ? css`
    font-family: ${theme.typography.fontFamily.gallery.display};
    font-size: ${theme.typography.fontSize.gallery.section};
    font-weight: ${theme.typography.fontWeight.gallery.semibold};
    line-height: ${theme.typography.lineHeight.snug};
    color: ${galleryColors.charcoal({ theme })};
    letter-spacing: -0.005em;
    margin: 0 0 ${gallerySpacing.xs({ theme })};
    
    @media (min-width: ${theme.breakpoints.desktop}) {
      font-size: ${theme.typography.fontSize.gallery.display};
    }
  ` : css`
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.text};
    margin: 0 0 ${theme.spacing.sm};
  `}
`;

const SectionDescription = styled.p<{ $galleryMode?: boolean }>`
  ${({ $galleryMode, theme }) => $galleryMode ? css`
    font-family: ${theme.typography.fontFamily.gallery.body};
    font-size: ${theme.typography.fontSize.gallery.metadata};
    font-weight: ${theme.typography.fontWeight.gallery.light};
    line-height: ${theme.typography.lineHeight.relaxed};
    color: ${galleryColors.neutralDark({ theme })};
    margin: 0;
    max-width: 600px;
  ` : css`
    font-size: ${theme.typography.fontSize.base};
    color: ${theme.colors.gray.dark};
    margin: 0;
    line-height: 1.5;
  `}
`;

// Grid item wrapper with stagger animation support
const GridItem = styled(motion.div)<{ $galleryMode?: boolean }>`
  ${({ $galleryMode }) => $galleryMode && css`
    break-inside: avoid;
    margin-bottom: ${gallerySpacing.artworkGenerous};
  `}
`;

export interface GalleryGridProps {
  children: React.ReactNode;
  galleryMode?: boolean;
  variant?: 'grid' | 'masonry';
  section?: {
    title?: string;
    description?: string;
  };
  className?: string;
  animate?: boolean;
}

export function GalleryGrid({
  children,
  galleryMode = false,
  variant = 'grid',
  section,
  className,
  animate = true,
}: GalleryGridProps) {
  // Animation variants for staggered reveals
  const containerVariants = useMemo(() => ({
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: galleryMode ? 0.1 : 0.05,
        delayChildren: 0.1,
      },
    },
  }), [galleryMode]);

  const itemVariants = useMemo(() => ({
    hidden: { 
      opacity: 0, 
      y: galleryMode ? 30 : 20,
      scale: galleryMode ? 0.95 : 1
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: galleryMode ? 0.6 : 0.3,
        ease: galleryMode ? [0.4, 0, 0.2, 1] : 'easeOut'
      }
    },
  }), [galleryMode]);

  // Convert children to array for animation support
  const childArray = React.Children.toArray(children);

  const content = (
    <GridContainer 
      $galleryMode={galleryMode} 
      $variant={variant}
      className={className}
    >
      {animate && galleryMode ? (
        // Animated grid items for gallery mode
        childArray.map((child, index) => (
          <GridItem
            key={index}
            $galleryMode={galleryMode}
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            custom={index}
          >
            {child}
          </GridItem>
        ))
      ) : (
        // Standard children rendering
        children
      )}
    </GridContainer>
  );

  if (section) {
    return (
      <GallerySection
        $galleryMode={galleryMode}
        variants={animate ? containerVariants : undefined}
        initial={animate ? "hidden" : undefined}
        animate={animate ? "visible" : undefined}
      >
        <SectionHeader $galleryMode={galleryMode}>
          {section.title && (
            <SectionTitle $galleryMode={galleryMode}>
              {section.title}
            </SectionTitle>
          )}
          {section.description && (
            <SectionDescription $galleryMode={galleryMode}>
              {section.description}
            </SectionDescription>
          )}
        </SectionHeader>
        {content}
      </GallerySection>
    );
  }

  if (animate && galleryMode) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {content}
      </motion.div>
    );
  }

  return content;
}

// Specialized masonry grid for variable height content
export interface MasonryGridProps {
  children: React.ReactNode;
  galleryMode?: boolean;
  className?: string;
  animate?: boolean;
}

export function MasonryGrid({
  children,
  galleryMode = false,
  className,
  animate = true,
}: MasonryGridProps) {
  return (
    <GalleryGrid
      galleryMode={galleryMode}
      variant="masonry"
      className={className}
      animate={animate}
    >
      {children}
    </GalleryGrid>
  );
}

// Responsive artwork grid with smart column sizing
const ResponsiveGridContainer = styled.div<{ $galleryMode?: boolean; $minWidth?: string }>`
  display: grid;
  gap: ${({ theme, $galleryMode }) => 
    $galleryMode ? gallerySpacing.artworkGenerous({ theme }) : theme.spacing.lg};
  
  ${({ $minWidth = '280px', $galleryMode }) => css`
    grid-template-columns: repeat(auto-fill, minmax(${$minWidth}, 1fr));
    
    ${$galleryMode && css`
      @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
        grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      }
      
      @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
      }
      
      @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
      }
    `}
  `}
`;

export interface ResponsiveArtworkGridProps {
  children: React.ReactNode;
  galleryMode?: boolean;
  minColumnWidth?: string;
  className?: string;
}

export function ResponsiveArtworkGrid({
  children,
  galleryMode = false,
  minColumnWidth = '280px',
  className,
}: ResponsiveArtworkGridProps) {
  return (
    <ResponsiveGridContainer
      $galleryMode={galleryMode}
      $minWidth={minColumnWidth}
      className={className}
    >
      {children}
    </ResponsiveGridContainer>
  );
}

// Gallery exhibition layout with featured sections
const ExhibitionLayout = styled.div<{ $galleryMode?: boolean }>`
  ${({ $galleryMode }) => $galleryMode && css`
    background-color: ${galleryColors.white};
    min-height: 100vh;
    padding: ${gallerySpacing.section} 0;
  `}
`;

const ExhibitionContainer = styled.div<{ $galleryMode?: boolean }>`
  ${({ $galleryMode }) => $galleryMode && css`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 ${gallerySpacing.margin};
    
    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
      padding: 0 ${gallerySpacing.gutter};
    }
  `}
`;

const FeaturedSection = styled.section<{ $galleryMode?: boolean }>`
  ${({ $galleryMode }) => $galleryMode && css`
    margin-bottom: ${gallerySpacing.xl};
    
    &:first-child {
      margin-top: 0;
    }
  `}
`;

export interface ExhibitionGridProps {
  children: React.ReactNode;
  galleryMode?: boolean;
  title?: string;
  subtitle?: string;
  featured?: boolean;
  className?: string;
}

export function ExhibitionGrid({
  children,
  galleryMode = false,
  title,
  subtitle,
  featured = false,
  className,
}: ExhibitionGridProps) {
  if (!galleryMode) {
    // Backward compatible standard grid
    return (
      <div className={className}>
        {title && <h2>{title}</h2>}
        {subtitle && <p>{subtitle}</p>}
        <ResponsiveArtworkGrid galleryMode={false}>
          {children}
        </ResponsiveArtworkGrid>
      </div>
    );
  }

  return (
    <ExhibitionLayout $galleryMode={galleryMode} className={className}>
      <ExhibitionContainer $galleryMode={galleryMode}>
        {featured ? (
          <FeaturedSection $galleryMode={galleryMode}>
            <GalleryGrid
              galleryMode={galleryMode}
              section={title || subtitle ? { title, description: subtitle } : undefined}
              animate={true}
            >
              {children}
            </GalleryGrid>
          </FeaturedSection>
        ) : (
          <GalleryGrid
            galleryMode={galleryMode}
            section={title || subtitle ? { title, description: subtitle } : undefined}
            animate={true}
          >
            {children}
          </GalleryGrid>
        )}
      </ExhibitionContainer>
    </ExhibitionLayout>
  );
}